import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Collapse, FormControl, Select, useDisclosure,
} from '@chakra-ui/react';
import { useForm, useFormState } from 'react-final-form';
import { FiMinus, FiPlus } from 'react-icons/fi';
import { NIL } from 'uuid';
import FieldLabel from './FieldLabel';
import FormRow from './FormRow';
import deferFormChanges from './deferFormChanges'; // item 1314
import ResponsiveStack from '../ResponsiveStack';
import TextInputControl from './TextInput';
import PlacesAttach from './PlacesAutocomplete';
import useContacts from '../../hooks/useContacts';
import { CustomerType } from '../../models/contactInfo'; // item 1370
import useContact from '../../hooks/useContact';

// item 465 (Chad 2026-07-05) — the SERVICE ADDRESS = the end-user this document is for:
// the customer's OWN client (e.g. National Technology Associates' end-user "Game On").
// It is distinct from the customer (the B2B client). The NAME is required — it IS the
// document's identifying title (proposalTitle / workOrderTitle / serviceName). The address,
// phone, and email are OPTIONAL and stay collapsed behind a disclosure so the form is clean.
// Rendered FIRST on every document form, above Basic Information. (RNWY contracts will add
// required service-address fields later — handled at test time, not here.)
interface Props {
  // the field the NAME binds to — the doc's title column for proposals/work orders, or the
  // dedicated `serviceName` for invoices/POs/bills.
  nameField: string;
  nameLabel?: string;
  subHeading?: string;
  // item 648/650 — the proposal form runs full-width (heading on top, fields span the page); the
  // other four doc forms keep the classic two-column label layout. Off by default.
  fullWidth?: boolean;
  // item 672 — override the section heading (e.g. "Shipping Address" on an automated proposal).
  heading?: string;
  // item 672 — when set, render a PO-number input under the address (binds to this form field).
  poNumberField?: string;
  // item 784 (Chad) — a "Ship to" dropdown of ALL CRM records (item 1370: vendors included,
  // employees excluded): picking one fills the
  // name + address + phone/email from that customer (drop-ship in one click). The Name input
  // stays free-typed for one-offs (e.g. "Pulse Group - Stock"). PO form only — on proposals
  // the name field is the document TITLE, not a recipient.
  customerPicker?: boolean;
}

const ServiceAddressSection: React.FC<Props> = ({
  nameField, nameLabel = 'Name', subHeading, fullWidth, heading = 'Service Address', poNumberField,
  customerPicker,
}) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const v = (values ?? {}) as Record<string, any>;
  const addr = (v.serviceAddress ?? {}) as Record<string, any>;
  const hasDetail = !!(addr.street || addr.unit || addr.city || addr.state || addr.zip
    || v.servicePhone || v.serviceEmail);

  // item 784 — the drop-ship customer picker: choosing a customer fetches their detail
  // (the lightweight contacts list carries no address) and fills name/address/contact.
  const { data: contactList } = useContacts();
  const [pickId, setPickId] = useState<string>(NIL);
  const { data: picked } = useContact({ id: customerPicker ? pickId : NIL });
  const pickedApplied = useRef<string>(NIL);

  // Start collapsed; auto-open ONCE if the loaded document already carries optional detail
  // (the doc loads async, so we can't rely on defaultIsOpen alone). Never fight a manual close.
  const { isOpen, onOpen, onToggle } = useDisclosure();
  const autoOpened = useRef(false);
  useEffect(() => {
    if (hasDetail && !autoOpened.current) {
      autoOpened.current = true;
      onOpen();
    }
  }, [hasDetail, onOpen]);

  // item 784 — apply the picked customer once its detail arrives (once per pick)
  useEffect(() => {
    if (!customerPicker || !picked || picked.id === NIL || picked.id !== pickId) return;
    if (pickedApplied.current === picked.id) return;
    pickedApplied.current = picked.id;
    const src = picked as Record<string, any>;
    const a = (src.shippingAddress && (src.shippingAddress.street || src.shippingAddress.city
      || src.shippingAddress.state || src.shippingAddress.zip))
      ? src.shippingAddress : src.serviceAddress;
    // item 1314 — deferred one tick so no field swallows it as its 'first notification'
    deferFormChanges(form, () => {
      // item 1321 (Chad, PO-100535: "what happened to the name") — picking a customer fills the
      // ADDRESS + contact; it only supplies the NAME when the name is still blank. A PO cut from a
      // Sales Order carries the JOB as its name — the vendor ships to the customer's address, but the
      // document keeps saying what job it's for.
      if (!(v[nameField] ?? '').toString().trim()) form.change(nameField, picked.name || '');
      // item 795 (Chad) — set each subfield individually, NOT the whole serviceAddress object:
      // final-form doesn't repaint the nested field inputs on a wholesale parent replace, so the
      // address filled but showed blank until a cell was focused.
      form.change('serviceAddress.street', a?.street || '');
      form.change('serviceAddress.unit', a?.unit || '');
      form.change('serviceAddress.city', a?.city || '');
      form.change('serviceAddress.state', a?.state || '');
      form.change('serviceAddress.zip', a?.zip || '');
      form.change('serviceAddress.country', a?.country || '');
      form.change('servicePhone', src.phone || '');
      form.change('serviceEmail', src.email || '');
    });
    onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, pickId, customerPicker]);

  return (
    <FormRow
      fullWidth={fullWidth}
      heading={heading}
      subHeading={subHeading
        ?? 'The end-user this is for — the customer’s client. Name is required; address & contact are optional.'}
    >
      <ResponsiveStack>
        <TextInputControl name={nameField} label={nameLabel} isRequired />
        {customerPicker && (
          <FormControl maxW={{ base: '100%', md: '320px' }}>
            {/* item 785 — FieldLabel keeps the picker on the same baseline as Name */}
            <FieldLabel>Ship to</FieldLabel>
            <Select
              placeholder="Fill from CRM…"
              value={pickId === NIL ? '' : pickId}
              onChange={(e) => {
                pickedApplied.current = NIL; // a re-pick of the same customer re-applies
                setPickId(e.target.value || NIL);
              }}
            >
              {(contactList?.index ?? [])
                // item 1370 (Chad) — the drop-ship target is ANY CRM record, vendors included:
                // "Pulse Group - Warehouse" is a Vendor-typed contact, so the old `!c.isVendor`
                // filter made our OWN warehouse impossible to ship to. Only EMPLOYEES are excluded
                // (they're staff, not a shipping destination). Archived contacts are already
                // filtered server-side (item 1067a).
                .filter((c) => c.customerType !== CustomerType.Employee)
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FormControl>
        )}
      </ResponsiveStack>
      <Button
        variant="link"
        size="sm"
        mt={3}
        leftIcon={isOpen ? <FiMinus /> : <FiPlus />}
        onClick={onToggle}
      >
        {isOpen ? 'Hide address & contact' : 'Add address & contact'}
      </Button>
      <Collapse in={isOpen} animateOpacity>
        <Box pt={3}>
          {/* dart (2026-07-14) — Google Places autocomplete on the Street field, so a
              drop-ship / end-user address can be looked up instead of typed (item 207 pattern). */}
          <PlacesAttach
            targetId="serviceAddress.street"
            onSelect={(place) => {
              form.change('serviceAddress.street', place.street || '');
              form.change('serviceAddress.unit', place.unit || '');
              form.change('serviceAddress.city', place.city || '');
              form.change('serviceAddress.state', place.state || '');
              form.change('serviceAddress.zip', place.zip || '');
              form.change('serviceAddress.country', place.country || '');
            }}
          />
          <ResponsiveStack>
            <TextInputControl name="serviceAddress.street" label="Street" isRequired={false} />
            <TextInputControl name="serviceAddress.unit" label="Unit" isRequired={false} />
          </ResponsiveStack>
          <ResponsiveStack>
            <TextInputControl name="serviceAddress.city" label="City" isRequired={false} />
            <TextInputControl name="serviceAddress.state" label="State" isRequired={false} />
            <TextInputControl name="serviceAddress.zip" label="Zip" isRequired={false} />
          </ResponsiveStack>
          <ResponsiveStack>
            <TextInputControl name="servicePhone" label="Phone" isRequired={false} />
            <TextInputControl name="serviceEmail" label="Email" isRequired={false} />
          </ResponsiveStack>
        </Box>
      </Collapse>
      {/* item 672 — the client's PO number, printed under the shipping address on the PDF.
          item 785 (Chad) — labeled CLIENT PO: the bare "PO Number" read like the document's
          own number (which auto-generates in Basic Information below). */}
      {poNumberField && (
        <Box pt={3} maxW={{ base: '100%', md: '360px' }}>
          <TextInputControl
            name={poNumberField}
            label="Client PO Number"
            isRequired={false}
            placeholder="The client's PO reference (optional)"
            helperText="Printed under the shipping address. Not this document's number — that auto-generates below."
          />
        </Box>
      )}
    </FormRow>
  );
};

ServiceAddressSection.defaultProps = {
  nameLabel: 'Name',
  subHeading: undefined,
  fullWidth: false,
  heading: 'Service Address',
  poNumberField: undefined,
  customerPicker: false,
};

export default ServiceAddressSection;
